/**
 * Main API service module for handling all API calls in the application
 * This centralizes API communication and provides consistent error handling
 */

import config from '../config';

// Cache mechanism to store API responses
const apiCache = {
  data: {},

  // Set cache data with optional expiration time (default 15 minutes)
  set(key, value, expiresInMs = 15 * 60 * 1000) {
    this.data[key] = {
      value,
      expiry: Date.now() + expiresInMs,
    };
  },

  // Get cached data if it exists and hasn't expired
  get(key) {
    const cached = this.data[key];
    if (!cached) return null;

    // Check if cache has expired
    if (cached.expiry < Date.now()) {
      delete this.data[key];
      return null;
    }

    return cached.value;
  },

  // Clear all cached data or specific key
  clear(key = null) {
    if (key) {
      delete this.data[key];
    } else {
      this.data = {};
    }
  },
};

/**
 * Base API request function with caching and error handling
 *
 * @param {string} url - API endpoint
 * @param {Object} options - Fetch options
 * @param {string} cacheKey - Optional cache key
 * @param {number} cacheDuration - Cache duration in milliseconds
 * @returns {Promise<any>} - API response
 */
async function apiRequest(url, options = {}) {
  try {
    console.log(
      `%c API Request: ${url}`,
      'background: #4299E1; color: white; padding: 2px 4px; border-radius: 2px;',
    );

    // Set default headers
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Add auth token if available
    // Changed from 'authToken' to 'token' to match what's used in AuthContext.js
    const token = localStorage.getItem('token');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    console.log('Request options:', {
      method: options.method || 'GET',
      headers,
      body: options.body ? '(request has body)' : '(no body)',
    });

    // Make the request
    const response = await fetch(url, {
      ...options,
      headers,
    });

    console.log(
      `%c Response status: ${response.status}`,
      response.ok
        ? 'background: #38A169; color: white; padding: 2px 4px; border-radius: 2px;'
        : 'background: #E53E3E; color: white; padding: 2px 4px; border-radius: 2px;',
    );

    // Handle non-2xx responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('API error response:', errorData);
      throw {
        status: response.status,
        message: errorData.error || `API error: ${response.status}`,
        data: errorData,
      };
    }

    // Parse JSON response
    const data = await response.json();
    console.log(
      '%c API Response data:',
      'background: #38A169; color: white; padding: 2px 4px; border-radius: 2px;',
      data,
    );

    return data;
  } catch (error) {
    console.error(
      `%c API Error (${url}):`,
      'background: #E53E3E; color: white; padding: 2px 4px; border-radius: 2px;',
      error,
    );

    throw error;
  }
}

export { apiRequest, apiCache };
